/**
 * Accesores tipados de los design tokens DTCG del Admin Web.
 *
 * La fuente única de verdad es `tokens.dtcg.json`; este módulo solo la aplana en
 * constantes cómodas de consumir y expone utilidades de tema (variables CSS).
 */
import rawTokens from './tokens.dtcg.json';

export type ThemeName = 'light' | 'dark';

/** Tokens DTCG crudos (por si un consumidor necesita el árbol completo). */
export const designTokens = rawTokens;

/** Color de marca. */
export const brandColors = {
  primary: rawTokens.color.brand.primary.$value,
  primaryHover: rawTokens.color.brand.primaryHover.$value,
  info: rawTokens.color.brand.info.$value,
  accent: rawTokens.color.brand.accent.$value,
  accentStrong: rawTokens.color.brand.accentStrong.$value,
  accentText: rawTokens.color.brand.accentText.$value,
} as const;

/** Color semántico (éxito, error, advertencia). */
export const semanticColors = {
  success: rawTokens.color.semantic.success.$value,
  successHover: rawTokens.color.semantic.successHover.$value,
  danger: rawTokens.color.semantic.danger.$value,
  dangerFill: rawTokens.color.semantic.dangerFill.$value,
  warning: rawTokens.color.semantic.warning.$value,
  warningText: rawTokens.color.semantic.warningText.$value,
} as const;

/** Neutros del tema claro. */
export const neutralColors = {
  bg: rawTokens.color.neutral.bg.$value,
  surface: rawTokens.color.neutral.surface.$value,
  surfaceHover: rawTokens.color.neutral.surfaceHover.$value,
  controlBorder: rawTokens.color.neutral.controlBorder.$value,
  border: rawTokens.color.neutral.border.$value,
  text: rawTokens.color.neutral.text.$value,
  textMuted: rawTokens.color.neutral.textMuted.$value,
} as const;

/** Rampa del tema oscuro (rediseño v2, Guía §7). */
export const darkColors = {
  bg: rawTokens.color.dark.bg.$value,
  surface: rawTokens.color.dark.surface.$value,
  surfaceHover: rawTokens.color.dark.surfaceHover.$value,
  controlBorder: rawTokens.color.dark.controlBorder.$value,
  surfaceElevated: rawTokens.color.dark.surfaceElevated.$value,
  border: rawTokens.color.dark.border.$value,
  text: rawTokens.color.dark.text.$value,
  textMuted: rawTokens.color.dark.textMuted.$value,
  primary: rawTokens.color.dark.primary.$value,
  primaryHover: rawTokens.color.dark.primaryHover.$value,
  link: rawTokens.color.dark.link.$value,
  info: rawTokens.color.dark.info.$value,
  accentOnDark: rawTokens.color.dark.accentOnDark.$value,
  success: rawTokens.color.dark.success.$value,
  warning: rawTokens.color.dark.warning.$value,
  danger: rawTokens.color.dark.danger.$value,
  dangerFill: rawTokens.color.dark.dangerFill.$value,
} as const;

/**
 * Colores del shell (sidebar/header): navy de marca en tema claro y near-black en
 * oscuro, donde todo el shell comparte el mismo fondo (Guía §7).
 */
export const shellColors = {
  light: {
    bg: rawTokens.color.shell.light.bg.$value,
    bgElevated: rawTokens.color.shell.light.bgElevated.$value,
    border: rawTokens.color.shell.light.border.$value,
    text: rawTokens.color.shell.light.text.$value,
    textMuted: rawTokens.color.shell.light.textMuted.$value,
  },
  dark: {
    bg: rawTokens.color.dark.bg.$value,
    border: rawTokens.color.dark.border.$value,
    text: rawTokens.color.dark.text.$value,
    textMuted: rawTokens.color.dark.textMuted.$value,
  },
} as const;

/** Tipografía. */
export const typography = {
  fontFamilyDisplay: rawTokens.typography.fontFamily.display.$value,
  fontFamilySans: rawTokens.typography.fontFamily.sans.$value,
  fontFamilyMono: rawTokens.typography.fontFamily.mono.$value,
  fontWeight: rawTokens.typography.fontWeight,
  fontSize: rawTokens.typography.fontSize,
  lineHeight: rawTokens.typography.lineHeight,
} as const;

/** Espaciado (grilla base de 8px). */
export const spacing = rawTokens.space;

/** Radios. */
export const radius = rawTokens.radius;

/** Sombras. */
export const shadows = rawTokens.shadow;

/** Breakpoints (desktop-first). */
export const breakpoints = rawTokens.breakpoint;

/**
 * Variables CSS del tema. Se aplican en `document.documentElement` para que el chrome
 * del layout (fondos, bordes, tipografía) siga los tokens sin depender de antd.
 * La nomenclatura es `--ge-<categoría>-<token>`.
 */
export function getThemeCssVariables(theme: ThemeName): Record<string, string> {
  const isDark = theme === 'dark';
  const shell = isDark ? shellColors.dark : shellColors.light;
  return {
    '--ge-color-bg': isDark ? darkColors.bg : neutralColors.bg,
    '--ge-color-surface': isDark ? darkColors.surface : neutralColors.surface,
    // Hover de fila (tablas y listas): mismo token que usa `Table.rowHoverBg`. Expuesto para
    // que cualquier fila pueda aclarar/oscurecer en dark sin inventar un color.
    '--ge-color-surface-hover': isDark ? darkColors.surfaceHover : neutralColors.surfaceHover,
    '--ge-color-surface-elevated': isDark ? darkColors.surfaceElevated : neutralColors.surface,
    '--ge-color-border': isDark ? darkColors.border : neutralColors.border,
    '--ge-color-control-border': isDark ? darkColors.controlBorder : neutralColors.controlBorder,
    '--ge-color-text': isDark ? darkColors.text : neutralColors.text,
    '--ge-color-text-muted': isDark ? darkColors.textMuted : neutralColors.textMuted,
    '--ge-color-primary': isDark ? darkColors.primary : brandColors.primary,
    '--ge-color-primary-hover': isDark ? darkColors.primaryHover : brandColors.primaryHover,
    '--ge-color-link': isDark ? darkColors.link : brandColors.info,
    '--ge-color-accent': brandColors.accent,
    // Oro oscuro legible sobre claro (tags/advertencias en texto normal).
    '--ge-color-accent-text': brandColors.accentText,
    // Semánticos: cada tema tiene su propia variante para cumplir AA sobre su superficie.
    '--ge-color-success': isDark ? darkColors.success : semanticColors.success,
    '--ge-color-warning': isDark ? darkColors.warning : semanticColors.warning,
    '--ge-color-danger': isDark ? darkColors.danger : semanticColors.danger,
    // Fill sólido de botones danger (texto blanco ≥4.5:1 en ambos temas).
    '--ge-color-danger-fill': isDark ? darkColors.dangerFill : semanticColors.dangerFill,
    // Shell (sidebar/header): navy en claro, near-black unificado en oscuro.
    '--ge-color-shell-bg': shell.bg,
    '--ge-color-shell-bg-elevated': isDark ? darkColors.surfaceElevated : shellColors.light.bgElevated,
    '--ge-color-shell-border': shell.border,
    '--ge-color-shell-text': shell.text,
    '--ge-color-shell-text-muted': shell.textMuted,
    // Header: blanco (surface) en claro; en oscuro comparte el fondo del shell.
    '--ge-color-header-bg': isDark ? darkColors.bg : neutralColors.surface,
    '--ge-font-display': typography.fontFamilyDisplay,
    '--ge-font-sans': typography.fontFamilySans,
    '--ge-font-mono': typography.fontFamilyMono,
    '--ge-radius': radius.default.$value,
    '--ge-shadow-sm': shadows.sm.$value,
    '--ge-shadow-md': shadows.md.$value,
    '--ge-shadow-lg': shadows.lg.$value,
  };
}

/** Aplica las variables CSS del tema a un elemento (por defecto `<html>`). */
export function applyThemeCssVariables(theme: ThemeName, target: HTMLElement = document.documentElement): void {
  for (const [name, value] of Object.entries(getThemeCssVariables(theme))) {
    target.style.setProperty(name, value);
  }
}
